import { AnimatePresence, motion } from "framer-motion";
import { createPortal } from "react-dom";
import { cn } from "../../utils/classnames";
import { DURATION, EASE_OUT } from "./motionPresets";
import { useBodyScrollLock, useCloseOnEscape } from "./modalHooks";

/**
 * Full-screen photo viewer shell (fleet galleries). Same family as
 * MotionModal — charcoal tint + blur instead of black, fades in and out,
 * rendered on <body> — but with no white panel: the children (photo,
 * arrows, ✕) sit directly on the backdrop.
 *
 * Clicks on the backdrop itself close it; stop propagation on the photo and
 * controls. A gallery that already handles Escape / page scrolling itself
 * (with arrow keys, say) can turn those off here.
 */
const MotionLightbox = ({
  show,
  onClose,
  label = "Image preview",
  closeOnEscape = true,
  lockScroll = true,
  className,
  children,
}) => {
  useBodyScrollLock(show && lockScroll);
  useCloseOnEscape(show && closeOnEscape, onClose);

  return createPortal(
    <AnimatePresence>
      {show && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label={label}
          // A little darker than MotionModal's tint so photos stand out,
          // but never solid black.
          className={cn(
            "tw:fixed tw:inset-0 tw:z-[9999] tw:flex tw:items-center tw:justify-center tw:bg-[#1F1F1F]/80 tw:backdrop-blur-[6px]",
            className
          )}
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: DURATION.fast, ease: EASE_OUT } }}
          exit={{ opacity: 0, transition: { duration: 0.2, ease: EASE_OUT } }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default MotionLightbox;
